import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import ApplicationContainer from '../../../state/containers/ApplicationContainer';
import AppliedApplicationTemplateContainer from '../../../state/containers/AppliedApplicationTemplateContainer';
import ApplicationEnvironmentsContainer from '../../../state/containers/ApplicationEnvironmentsContainer';
import PageError from '../../../components/general/PageError';
import { aiAnsweringCommonAppName, aiDentalSchedulingCommonAppName } from '../../../constants/sparkConstants/moduleConsts';
import TemplateFormFieldModel from '../../../models/templating/api/TemplateFormFieldModel';
import MedicalIntegrationConfigContainer from '../../../hooks/MedicalIntegrationConfigContainer';
import MedicalIntegrationConfigSettings from './MedicalIntegrationConfigSettings';
import useBulkAppOperations from '../../../hooks/useBulkAppOperations';
import * as XLSX from 'xlsx';
import BulkFeatureFlagSettingsPanel from './BulkFeatureFlagSettingsPanel';
import ManagedWorkQueuePanel from './ManagedWorkQueuePanel';
import ManagementWorkspace, { ManagementTool } from './management/ManagementWorkspace';
import LocationSidebar from './management/LocationSidebar';
import LocationTopicsTool from './management/LocationTopicsTool';
import { filterApplicationsBySearch } from './management/filterApplicationsBySearch';
import {
    ui, ActionRow, ActivityLog, Group, GroupCaption, GroupSection, GroupTitle
} from './management/ManagementUI';

interface OrganizationHealthcareManagementSettingsProps {
    organizationId: string;
    organizationName?: string;
    organizations?: Array<{ id: string; name: string }>;
    canManageQueues?: boolean;
    applicationContainer: ApplicationContainer;
    appliedTemplateContainer?: AppliedApplicationTemplateContainer;
    applicationEnvironmentsContainer?: ApplicationEnvironmentsContainer;
}

const OrganizationHealthcareManagementSettingsInner: React.FC<OrganizationHealthcareManagementSettingsProps> = ({
    organizationId,
    organizationName,
    organizations,
    canManageQueues = false,
    applicationContainer,
    appliedTemplateContainer,
    applicationEnvironmentsContainer
}) => {
    const [errors, setErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
    const [sharedTopicsApp, setSharedTopicsApp] = useState<ApplicationModel | null>(null);
    const [locationSpecificFields, setLocationSpecificFields] = useState<TemplateFormFieldModel[]>([]);
    const [isLoadingSharedTopics, setIsLoadingSharedTopics] = useState(false);
    const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState<string>('');
    const [importedData, setImportedData] = useState<any[] | null>(null);
    const [isApplying, setIsApplying] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [activeToolId, setActiveToolId] = useState<string>('');

    // Bulk app operations hook
    const { isProcessing: isPublishing, logs: bulkOperationLogs, syncApps, publishApps, clearLogs } = useBulkAppOperations({
        organizationId,
        appliedTemplateContainer,
        applicationEnvironmentsContainer
    });

    useEffect(() => {
        // Apps are already loaded by parent component (OrganizationSettingsTabs)
        if (appliedTemplateContainer) {
            loadSharedTopicsFields();
        }
    }, [organizationId]);

    // Filter applications for healthcare organizations
    const orgApplications = applicationContainer.state.baseOrgApplications;
    const healthcareApps = useMemo(() => orgApplications.filter(app => {
        // Filter out any apps that might be system apps
        const name = app?.name?.toLowerCase() ?? '';
        return !(name.includes("manage menu") || name.includes("manage entities") || (name.startsWith("x -") && name.endsWith("menu")));
    }), [orgApplications]);

    const filteredApps = useMemo(() => filterApplicationsBySearch(healthcareApps, searchTerm), [healthcareApps, searchTerm]);

    const loadSharedTopicsFields = async () => {
        setIsLoadingSharedTopics(true);
        try {
            const appsResult = await applicationContainer.searchForOrgApplications(organizationId, 1, 50, undefined, undefined, true);
            const apps = appsResult?.data?.applications ?? [];

            let sharedTopics = apps.find(app =>
                app.name?.toLowerCase()?.trim() === aiDentalSchedulingCommonAppName.toLowerCase() && app.allowsTemplating
            );

            if (!sharedTopics) {
                sharedTopics = apps.find(app =>
                    app.name?.toLowerCase()?.includes('shared topics') && app.allowsTemplating
                );
            }

            if (!sharedTopics) {
                sharedTopics = apps.find(app =>
                    app.name?.toLowerCase()?.includes('shared topics')
                );
            }

            if (sharedTopics && appliedTemplateContainer) {
                setSharedTopicsApp(sharedTopics);

                const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(sharedTopics.id);

                if (appliedTemplatesResult?.resultType === "Ok") {
                    const appliedTemplates = appliedTemplatesResult.data;

                    const locationFields: TemplateFormFieldModel[] = [];
                    const fieldSet = new Set<string>();

                    appliedTemplates.forEach(appliedTemplate => {
                        appliedTemplate.templateForm?.templateFormSections?.forEach(section => {
                            section.templateFormFields?.forEach(field => {
                                const fieldValue = appliedTemplate.templateFormFieldValues?.find(
                                    v => v.templateFormFieldId === field.id
                                );

                                if (fieldValue?.associatedVariable && fieldValue.associatedVariable === fieldValue.value) {
                                    if (!fieldSet.has(field.title)) {
                                        fieldSet.add(field.title);
                                        locationFields.push({
                                            ...field,
                                            name: section.title
                                        });
                                    }
                                }
                            });
                        });
                    });

                    setLocationSpecificFields(locationFields);
                }
            }
        } catch (error) {
            console.error('Error loading shared topics fields:', error);
        } finally {
            setIsLoadingSharedTopics(false);
        }
    };

    const toggleFieldSelection = (fieldTitle: string) => {
        const newSelected = new Set(selectedFields);
        if (newSelected.has(fieldTitle)) {
            newSelected.delete(fieldTitle);
        } else {
            newSelected.add(fieldTitle);
        }
        setSelectedFields(newSelected);
    };

    const toggleAllFields = () => {
        if (selectedFields.size === locationSpecificFields.length) {
            setSelectedFields(new Set());
        } else {
            setSelectedFields(new Set(locationSpecificFields.map(field => field.title)));
        }
    };

    const publishTemplatesForSelectedLocations = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        const selectedApplications = healthcareApps.filter(app => selectedApps.has(app.id));
        await publishApps(selectedApplications);
    };

    const moduleSyncSelectedLocations = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        const selectedApplications = healthcareApps.filter(app => selectedApps.has(app.id));
        await syncApps(selectedApplications, sharedTopicsApp);
    };

    const importFromExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setIsImporting(true);
            setErrors([]);
            setImportProgress('Reading Excel file...');

            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length < 2) {
                setErrors(['Excel file is empty or has no data rows']);
                return;
            }

            const headers = jsonData[0] as string[];
            const dataRows = jsonData.slice(1);

            const locationIdIndex = headers.findIndex(h => h === 'Location ID');
            const locationNameIndex = headers.findIndex(h => h === 'Location Name');

            if (locationIdIndex === -1 || locationNameIndex === -1) {
                setErrors(['Excel file must have "Location ID" and "Location Name" columns']);
                return;
            }

            const fieldHeaders = headers.slice(2);

            setImportProgress(`Validating ${dataRows.length} locations...`);

            const validationErrors: string[] = [];
            const parsedData: any[] = [];

            for (let i = 0; i < dataRows.length; i++) {
                const row = dataRows[i];
                const locationId = row[locationIdIndex];
                const locationName = row[locationNameIndex];

                if (!locationId) continue;

                setImportProgress(`Validating ${i + 1}/${dataRows.length}: ${locationName || locationId}...`);

                let app = applicationContainer.state.baseOrgApplications.find(
                    a => a.id === locationId
                );

                if (!app) {
                    validationErrors.push(`Location not found: ${locationName || locationId}`);
                    continue;
                }

                const fieldChanges: { fieldTitle: string; newValue: string }[] = [];
                for (let fieldIndex = 0; fieldIndex < fieldHeaders.length; fieldIndex++) {
                    const fieldTitle = fieldHeaders[fieldIndex];
                    const newValue = row[2 + fieldIndex];

                    if (newValue !== undefined && newValue !== null && newValue !== '') {
                        fieldChanges.push({
                            fieldTitle,
                            newValue: String(newValue)
                        });
                    }
                }

                if (fieldChanges.length > 0) {
                    parsedData.push({
                        locationId,
                        locationName: locationName || app.name,
                        appId: app.id,
                        fieldChanges
                    });
                }
            }

            setImportProgress('');

            if (parsedData.length === 0) {
                setErrors(['No valid data found in Excel file']);
                return;
            }

            setImportedData(parsedData);

            if (validationErrors.length > 0) {
                setErrors([
                    `Loaded ${parsedData.length} locations from Excel with ${validationErrors.length} warnings. Click "Apply Changes" to save the updates.`,
                    ...validationErrors.slice(0, 10),
                    ...(validationErrors.length > 10 ? [`...and ${validationErrors.length - 10} more warnings`] : [])
                ]);
            } else {
                setErrors([`Successfully loaded ${parsedData.length} locations from Excel. Review the changes below and click "Apply Changes" to save.`]);
            }

        } catch (error) {
            console.error('Error importing Excel:', error);
            setErrors(['Error importing Excel: ' + error.toString()]);
        } finally {
            setIsImporting(false);
            setImportProgress('');
            event.target.value = '';
        }
    };

    const applyChanges = async () => {
        if (!importedData || importedData.length === 0) {
            setErrors(['No imported data to apply']);
            return;
        }

        try {
            setIsApplying(true);
            setErrors([]);
            setImportProgress('Applying changes...');

            let successCount = 0;
            let errorCount = 0;
            const applyErrors: string[] = [];

            for (let i = 0; i < importedData.length; i++) {
                const locationData = importedData[i];

                setImportProgress(`Applying ${i + 1}/${importedData.length}: ${locationData.locationName}...`);

                try {
                    const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(locationData.appId);

                    if (appliedTemplatesResult?.resultType !== "Ok") {
                        applyErrors.push(`Failed to load templates for ${locationData.locationName}`);
                        errorCount++;
                        continue;
                    }

                    const appliedTemplates = appliedTemplatesResult.data;

                    for (const fieldChange of locationData.fieldChanges) {
                        let updated = false;
                        for (const appliedTemplate of appliedTemplates) {
                            for (const section of appliedTemplate.templateForm?.templateFormSections || []) {
                                const field = section.templateFormFields?.find(f => f.title === fieldChange.fieldTitle);

                                if (field) {
                                    let fieldValue = appliedTemplate.templateFormFieldValues?.find(
                                        v => v.templateFormFieldId === field.id
                                    );

                                    if (!fieldValue) {
                                        fieldValue = {
                                            templateFormFieldId: field.id,
                                            value: fieldChange.newValue,
                                            associatedVariable: field.associatedVariable,
                                            label: field.label,
                                            fieldType: field.fieldType,
                                            placeholder: field.placeholder,
                                            defaultValue: field.defaultValue,
                                            tip: field.tip,
                                            title: field.title,
                                            appliedApplicationTemplateId: appliedTemplate.id
                                        };
                                        appliedTemplate.templateFormFieldValues.push(fieldValue);
                                    } else {
                                        fieldValue.value = fieldChange.newValue;
                                    }

                                    updated = true;
                                    break;
                                }
                            }
                            if (updated) break;
                        }
                    }

                    let allSuccess = true;
                    for (const appliedTemplate of appliedTemplates) {
                        const updateRequest = {
                            templateFormFieldValues: appliedTemplate.templateFormFieldValues,
                            dynamicFormSections: appliedTemplate.templateForm.templateFormSections.filter(s => s?.name)
                        };

                        const result = await appliedTemplateContainer.updateAppliedTemplate(
                            appliedTemplate.id,
                            updateRequest
                        );

                        if (result?.resultType !== "Ok") {
                            allSuccess = false;
                            break;
                        }
                    }

                    if (allSuccess) {
                        successCount++;
                    } else {
                        applyErrors.push(`Failed to save updates for ${locationData.locationName}`);
                        errorCount++;
                    }

                } catch (error) {
                    console.error(`Error applying changes for location ${locationData.locationName}:`, error);
                    applyErrors.push(`Error applying changes to ${locationData.locationName}: ${error.toString()}`);
                    errorCount++;
                }
            }

            setImportProgress('');

            if (applyErrors.length > 0) {
                setErrors([
                    `Applied changes with ${successCount} successes and ${errorCount} errors:`,
                    ...applyErrors.slice(0, 10),
                    ...(applyErrors.length > 10 ? [`...and ${applyErrors.length - 10} more errors`] : [])
                ]);
            } else {
                setErrors([`Successfully applied changes to ${successCount} locations!`]);
                setImportedData(null);
            }

        } catch (error) {
            console.error('Error applying changes:', error);
            setErrors(['Error applying changes: ' + error.toString()]);
        } finally {
            setIsApplying(false);
            setImportProgress('');
        }
    };

    const exportToExcel = async () => {
        if (selectedApps.size === 0 || selectedFields.size === 0) {
            setErrors(['Please select at least one application/location and one field to export']);
            return;
        }

        try {
            setIsLoadingSharedTopics(true);

            const selectedApplications = healthcareApps.filter(app => selectedApps.has(app.id));
            const selectedFieldsList = locationSpecificFields.filter(field => selectedFields.has(field.title));

            const headers = ['Location ID', 'Location Name', ...selectedFieldsList.map(field => field.title)];

            const data: any[][] = [];

            for (const app of selectedApplications) {
                const row: any[] = [
                    app.id,
                    app.name
                ];

                if (appliedTemplateContainer) {
                    const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(app.id);

                    if (appliedTemplatesResult?.resultType === "Ok") {
                        const appliedTemplates = appliedTemplatesResult.data;

                        for (const selectedField of selectedFieldsList) {
                            let fieldValue = '';

                            for (const template of appliedTemplates) {
                                const field = template.templateForm?.templateFormSections
                                    ?.flatMap(section => section.templateFormFields ?? [])
                                    ?.find(f => f.title === selectedField.title);

                                if (field) {
                                    const value = template.templateFormFieldValues?.find(
                                        v => v.templateFormFieldId === field.id
                                    );

                                    if (value?.value) {
                                        fieldValue = value.value;
                                        break;
                                    }
                                }
                            }

                            row.push(fieldValue);
                        }
                    } else {
                        selectedFieldsList.forEach(() => row.push(''));
                    }
                } else {
                    selectedFieldsList.forEach(() => row.push(''));
                }

                data.push(row);
            }

            const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Location Specific Topics');

            const timestamp = new Date().toISOString().split('T')[0];
            const fileName = `healthcare_location_topics_${timestamp}.xlsx`;

            XLSX.writeFile(wb, fileName);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            setErrors(['Error exporting to Excel: ' + error.toString()]);
        } finally {
            setIsLoadingSharedTopics(false);
        }
    };

    const selectedCount = healthcareApps.filter(app => selectedApps.has(app.id)).length;

    const renderSyncTool = () => (
        <>
            <GroupSection>
                <GroupTitle>Modules</GroupTitle>
                <Group>
                    <ActionRow
                        title="Module Sync"
                        description="Sync shared modules from Shared Topics to the selected locations."
                        actionText={`Sync ${selectedCount}`}
                        onAction={moduleSyncSelectedLocations}
                        disabled={selectedCount === 0 || isPublishing || !sharedTopicsApp}
                    />
                    <ActionRow
                        title="Backup and Publish"
                        description="Back up each selected location, then publish it."
                        actionText={`Publish ${selectedCount}`}
                        variant="primary"
                        onAction={publishTemplatesForSelectedLocations}
                        disabled={selectedCount === 0 || isPublishing || !applicationEnvironmentsContainer}
                    />
                </Group>
                <GroupCaption>
                    {selectedCount === 0
                        ? 'Choose locations from the list to get started.'
                        : !sharedTopicsApp && !isLoadingSharedTopics
                            ? 'Module sync needs a Shared Topics application in this organization.'
                            : `${selectedCount} ${selectedCount === 1 ? 'location' : 'locations'} selected.`}
                </GroupCaption>
            </GroupSection>

            <ActivityLog title="Activity" lines={bulkOperationLogs} isRunning={isPublishing} onClear={clearLogs} />
        </>
    );

    const tools: ManagementTool[] = [
        ...(canManageQueues ? [{ id: 'sync', label: 'Sync & Publish', render: renderSyncTool }] : []),
        {
            id: 'featureSettings',
            label: 'Feature Settings',
            description: 'Change one feature flag setting across many locations at once, e.g. "Missed Appointment Lookback Days" for every location with Outbound Calling.',
            render: () => (
                <BulkFeatureFlagSettingsPanel
                    organizationId={organizationId}
                    applicationContainer={applicationContainer}
                    applications={healthcareApps}
                    selectedAppIds={selectedApps}
                    onSelectedAppIdsChange={setSelectedApps}
                />
            )
        },
        {
            id: 'medicalIntegrations',
            label: 'Medical Integrations',
            description: 'Load, create and update this organization\'s medical integration configurations.',
            usesLocations: false,
            render: () => <MedicalIntegrationConfigSettings organizationId={organizationId} />
        },
        ...(appliedTemplateContainer ? [{
            id: 'topics',
            label: 'Location Topics',
            description: 'Export or import location-specific Shared Topics fields as a spreadsheet.',
            render: () => (
                <LocationTopicsTool
                    isLoading={isLoadingSharedTopics}
                    hasSharedTopicsApp={!!sharedTopicsApp}
                    fields={locationSpecificFields}
                    selectedFields={selectedFields}
                    onToggleField={toggleFieldSelection}
                    onToggleAllFields={toggleAllFields}
                    selectedLocationCount={selectedCount}
                    onExport={exportToExcel}
                    onImport={importFromExcel}
                    isImporting={isImporting}
                    isApplying={isApplying}
                    importProgress={importProgress}
                    importedData={importedData}
                    onApplyImport={applyChanges}
                />
            )
        }] : []),
        ...(canManageQueues ? [{
            id: 'workQueue',
            label: 'Work Queue',
            usesLocations: false,
            render: () => (
                <WorkQueueWrapper>
                    <ManagedWorkQueuePanel organizationId={organizationId} organizationName={organizationName} organizations={organizations} />
                </WorkQueueWrapper>
            )
        }] : [])
    ];

    return (
        <>
            <ManagementWorkspace
                title="Healthcare Management"
                subtitle="Select locations, then choose what to do with them."
                tools={tools}
                activeToolId={activeToolId}
                onActiveToolChange={setActiveToolId}
                sidebar={
                    <LocationSidebar
                        applications={healthcareApps}
                        visibleApplications={filteredApps}
                        selectedAppIds={selectedApps}
                        onSelectedAppIdsChange={setSelectedApps}
                        searchTerm={searchTerm}
                        onSearchTermChange={setSearchTerm}
                        emptyText="No healthcare applications found in this organization."
                    />
                }
            />

            {successMessage && (
                <PageError errors={[successMessage]} type="success" />
            )}
            <PageError errors={errors} />
        </>
    );
};

// Wrapper component that provides the MedicalIntegrationConfigContainer
const OrganizationHealthcareManagementSettings: React.FC<OrganizationHealthcareManagementSettingsProps> = (props) => {
    return (
        <MedicalIntegrationConfigContainer.Provider>
            <OrganizationHealthcareManagementSettingsInner {...props} />
        </MedicalIntegrationConfigContainer.Provider>
    );
};

const WorkQueueWrapper = styled.div`
    & > section {
        margin: 0;
        border: 0;
        border-radius: ${ui.radius};
        box-shadow: 0 0 0 0.5px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
    }

    /* Let the queue's search field shrink on narrow screens */
    input {
        min-width: 0;
    }
`;

export default OrganizationHealthcareManagementSettings;
